///
/// EarthBrowser - Root App Component
///
/// Created: 2025
/// Copyright (c) 2025 Geodata Labs
///

import { StoreProvider } from '@/app/StoreProvider'
import { ThemeProvider } from '@/app/ThemeProvider'
import { Loader } from '@/components/ui/loader'
import { Toaster } from '@/components/ui/toaster'
import { lazy, Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

// Dialogs
import { UserProfileDialog } from '@/components/dialogs/profile/UserProfileDialog'
import { BetaTestDialog } from '@/components/dialogs/signin/BetaTestDialog'
import { AuthDialog } from '@/components/dialogs/signin/AuthDialog'
import { DeviceLimitDialog } from '@/components/dialogs/signin/DeviceLimitDialog'
import { NativeAuthSheet } from '@/components/dialogs/signin/NativeAuthSheet'
import { isNativeApp } from '@/native'
import { SubscriptionDialog } from '@/components/dialogs/subscription/SubscriptionDialog'
import { OnboardingDialog } from '@/components/dialogs/onboarding/OnboardingDialog'
import { BetaTransitionBanner } from '@/components/beta/BetaTransitionBanner'
import { CookieConsent } from '@/components/misc/CookieConsent'
import { NoscriptWarning } from '@/components/misc/NoscriptWarning'
import { getBetaStatus } from '@/hooks/useBetaStatus'

const { isBeta } = getBetaStatus()

// Pages - lazy load for better code splitting
import Home from '@/app/page'

const Attribution = lazy(() => import('@/app/attribution/page'))
const Contact = lazy(() => import('@/app/contact/page'))
const Cookies = lazy(() => import('@/app/cookies/page'))
const ErrorPage = lazy(() => import('@/app/error/page'))
const Pricing = lazy(() => import('@/app/pricing/page'))
const Privacy = lazy(() => import('@/app/privacy/page'))
const Profile = lazy(() => import('@/app/profile/page'))
const Subscribe = lazy(() => import('@/app/subscribe/page'))
const TermsAndConditions = lazy(() => import('@/app/terms-and-conditions/page'))
const VerifyEmail = lazy(() => import('@/app/verify-email/page'))

function App() {
  return (
    <StoreProvider>
      <HelmetProvider>
        <ThemeProvider attribute='class' defaultTheme='dark' disableTransitionOnChange>
          <BrowserRouter>
            <Suspense fallback={<Loader />}>
              <NoscriptWarning />

              <Routes>
                {/* Main Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/attribution" element={<Attribution />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/subscribe" element={<Subscribe />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* Redirects (from next.config.js) */}
                <Route path="/purchase" element={<Navigate to="/pricing" replace />} />
                {/* Former desktop-download landing page (Tauri track parked; see morgue/tauri) */}
                <Route path="/download" element={<Navigate to="/" replace />} />
                <Route path="/desktop" element={<Navigate to="/" replace />} />
                <Route path="/install" element={<Navigate to="/" replace />} />
              </Routes>

              {/* Global Dialogs */}
              <CookieConsent />
              <BetaTestDialog />
              {/* Capacitor apps get the native-provider sheet; the web keeps the full dialog */}
              {isNativeApp() ? <NativeAuthSheet /> : <AuthDialog />}
              <DeviceLimitDialog />
              <OnboardingDialog />
              <SubscriptionDialog />
              <UserProfileDialog />
              {isBeta && <BetaTransitionBanner />}
              <Toaster />
            </Suspense>
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </StoreProvider>
  )
}

export default App
